/**
 * Rich Text Editor Component
 * A Lexical-based rich text editor with HTML export support
 */

import React, { useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $createParagraphNode, $isTextNode, $isParagraphNode } from 'lexical';

import EditorTheme from './theme/EditorTheme';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import './RichTextEditor.css';

/**
 * Generates clean, email-compatible HTML from Lexical editor state
 * Uses inline styles instead of CSS classes for maximum email client compatibility
 */
function generateEmailHTML(editorState) {
  let html = '';
  
  editorState.read(() => {
    const root = $getRoot();
    const children = root.getChildren();
    
    children.forEach((node) => {
      if ($isParagraphNode(node)) {
        const paragraphHTML = generateParagraphHTML(node);
        html += paragraphHTML;
      }
    });
  });
  
  return html;
}

/**
 * Generate HTML for a paragraph node with proper alignment
 */
function generateParagraphHTML(paragraphNode) {
  const format = paragraphNode.getFormatType();
  let style = 'margin: 0 0 8px 0;';
  
  // Add text alignment
  if (format === 'center') {
    style += ' text-align: center;';
  } else if (format === 'right') {
    style += ' text-align: right;';
  } else if (format === 'justify') {
    style += ' text-align: justify;';
  }
  
  const childrenHTML = paragraphNode.getChildren()
    .map(child => generateNodeHTML(child))
    .join('');
  
  return `<p style="${style}">${childrenHTML || '&nbsp;'}</p>`;
}

/**
 * Generate HTML for a single node (text node with formatting)
 */
function generateNodeHTML(node) {
  if ($isTextNode(node)) {
    let text = escapeHTML(node.getTextContent());
    const format = node.getFormat();
    
    // Apply formatting using inline styles for email compatibility
    // Check each format flag
    const isBold = (format & 1) !== 0;      // Bold flag
    const isItalic = (format & 2) !== 0;    // Italic flag
    const isStrikethrough = (format & 4) !== 0; // Strikethrough flag
    const isUnderline = (format & 8) !== 0;  // Underline flag
    
    if (isBold) {
      text = `<strong>${text}</strong>`;
    }
    if (isItalic) {
      text = `<em>${text}</em>`;
    }
    if (isUnderline) {
      text = `<u>${text}</u>`;
    }
    if (isStrikethrough) {
      text = `<s>${text}</s>`;
    }
    
    return text;
  }
  
  // For other node types, get text content
  return escapeHTML(node.getTextContent());
}

/**
 * Escape HTML special characters
 */
function escapeHTML(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Plugin to handle editor changes and expose methods to parent
 */
function OnChangePlugin({ onChange, editorRef }) {
  const [editor] = useLexicalComposerContext();

  // Store editor reference
  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  // Register update listener
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      // Check if editor is empty
      let isEmpty = true;
      editorState.read(() => {
        const root = $getRoot();
        const textContent = root.getTextContent().trim();
        isEmpty = !textContent;
      });
      
      if (isEmpty) {
        onChange('');
      } else {
        // Generate clean email-compatible HTML
        const htmlString = generateEmailHTML(editorState);
        onChange(htmlString);
      }
    });
  }, [editor, onChange]);

  return null;
}

/**
 * Plugin to clear the editor content
 */
function ClearPlugin({ clearTrigger }) {
  const [editor] = useLexicalComposerContext();
  
  useEffect(() => {
    if (clearTrigger > 0) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        root.append($createParagraphNode());
      });
    }
  }, [clearTrigger, editor]);

  return null;
}

/**
 * Placeholder component
 */
function Placeholder({ text }) {
  return <div className="editor-placeholder">{text}</div>;
}

/**
 * Main Rich Text Editor Component
 * 
 * @param {Object} props
 * @param {string} props.placeholder - Placeholder text when editor is empty
 * @param {function} props.onChange - Callback fired when content changes, receives HTML string
 * @param {number} props.clearTrigger - Increment this value to clear the editor
 * @param {string} props.namespace - Unique namespace for the editor instance
 */
const RichTextEditor = forwardRef(function RichTextEditor(
  { 
    placeholder = 'Gebe Deinen Text ein...', 
    onChange = () => {},
    clearTrigger = 0,
    namespace = 'ReminderEditor'
  }, 
  ref
) {
  const editorRef = useRef(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    // Get the current HTML content
    getHTML: () => {
      if (!editorRef.current) return '';
      return generateEmailHTML(editorRef.current.getEditorState());
    },
    // Clear the editor content
    clear: () => {
      if (!editorRef.current) return;
      editorRef.current.update(() => {
        const root = $getRoot();
        root.clear();
        root.append($createParagraphNode());
      });
    },
    // Focus the editor
    focus: () => {
      if (!editorRef.current) return;
      editorRef.current.focus();
    }
  }), []);

  const initialConfig = {
    namespace,
    theme: EditorTheme,
    onError: (error) => {
      console.error('Lexical Editor Error:', error);
    },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="rich-text-editor-container">
        <ToolbarPlugin />
        <div className="editor-content-wrapper">
          <RichTextPlugin
            contentEditable={
              <ContentEditable 
                className="editor-input"
                aria-placeholder={placeholder}
              />
            }
            placeholder={<Placeholder text={placeholder} />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <OnChangePlugin onChange={onChange} editorRef={editorRef} />
          <ClearPlugin clearTrigger={clearTrigger} />
        </div>
      </div>
    </LexicalComposer>
  );
});

export default RichTextEditor;
